import * as Sentry from '@sentry/react';

export const initSentry = () => {
  // Check cookie consent before initializing
  const cookieConsent = localStorage.getItem('cookie_consent_v1');
  const hasAnalyticsConsent = cookieConsent ? JSON.parse(cookieConsent).analytics : false;
  
  if (!hasAnalyticsConsent) {
    if (import.meta.env.DEV) {
      console.log('Sentry disabled: Analytics cookies not accepted');
    }
    return;
  }

  // Get DSN from environment - use VITE_ prefix only during development
  // In production builds, these will be replaced with actual values by Vite
  const dsn = typeof window !== 'undefined' && '__SENTRY_DSN__' in window
    ? (window as Window & { __SENTRY_DSN__?: string }).__SENTRY_DSN__
    : undefined;
  
  const isProd = import.meta.env.PROD;
  
  if (!dsn) {
    if (!isProd) {
      // Silent in development, no need to warn
      return;
    }
    if (import.meta.env.DEV) {
      console.warn('Sentry DSN not configured. Error tracking disabled.');
    }
    return;
  }

  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Performance Monitoring
    tracesSampleRate: isProd ? 0.1 : 1.0,
    
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    
    // Environment
    environment: isProd ? 'production' : 'development',
    
    // Filter out known non-critical errors
    beforeSend(event, hint) {
      const error = hint.originalException;
      
      // Don't send errors in development
      if (!isProd) {
        return null;
      }
      
      // Filter out network errors from ad blockers and browser extensions
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String(error.message);
        const ignoredPatterns = [
          'ResizeObserver',
          'Non-Error promise rejection',
          'cancelled',
          'AbortError',
          'Network request failed',
          'Failed to fetch',
        ];
        
        if (ignoredPatterns.some(pattern => message.includes(pattern))) {
          return null;
        }
      }
      
      return event;
    },
    
    // Additional security: Don't send PII
    sendDefaultPii: false,
  });
};

// Helper to capture exceptions manually
export const captureException = (error: Error, context?: Record<string, unknown>) => {
  Sentry.captureException(error, {
    contexts: context ? { custom: context } : undefined,
  });
};

// Helper to capture messages
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  Sentry.captureMessage(message, level);
};

// Helper to set user context
export const setUserContext = (user: { id: string; email?: string; name?: string }) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  });
};

// Helper to clear user context on logout
export const clearUserContext = () => {
  Sentry.setUser(null);
};
